'use client';

import React, { useState, useMemo } from 'react';

import { useSearchParams } from 'next/navigation';

import { TypePlayKitsPageData } from '@/app/[locale]/(shop)/account/subscriptions/fetchPageData';

import { usePageData } from '@/context/PageDataProvider';
import { usePageTranslations } from '@/context/PageTranslationsProvider';

import { TypePlayKitScheduleFields } from '@/core/generated-types';

import {
  isSubscriptionCancelled,
  isSubscriptionFinished,
} from '@/lib/api/subscriptionManager/helpers/subscriptionHelpers';

import { CTA_NAMES, PAGE_LOCATIONS } from '@/utils/rudderstack';

import { TypePlayKitPageTranslations } from '@/utils/translations/types/TypePlayKitPageTranslations';

import { Divider } from '../_components/Divider';
import { ItemSelector, TypeItem } from '../_components/ItemSelector';

import {
  LayoutActiveSubscription,
  LayoutAgedOutSubscription,
  LayoutCancelledSubscription,
  LayoutNoSubscription,
  SubscriptionPageHeading,
} from './components/layouts';

function PlayKitSchedule({
  childrenIcons,
  noticeBanner,
  holidayBanner,
  prepayBanner,
}: TypePlayKitScheduleFields) {
  const t = usePageTranslations() as TypePlayKitPageTranslations;

  const data = usePageData() as TypePlayKitsPageData;

  const { user, subscriptions } = data || {};

  const searchParams = useSearchParams();

  function determineDefaultSelectedChildUuid() {
    const defaultSelectedSubscription = subscriptions.find(
      (subscription) => subscription.isDefaultSelectedSubscription,
    );

    const updatedChildUuid = searchParams?.get('updated_child_uuid');

    const [firstVisibleChild] = user.visibleChildren;

    return (
      updatedChildUuid ||
      defaultSelectedSubscription?.child_uuid ||
      firstVisibleChild?.uuid
    );
  }

  const defaultSelectedChildUuid = determineDefaultSelectedChildUuid();

  const [selectedChildUuid, setSelectedChildUuid] = useState<string>(
    defaultSelectedChildUuid,
  );

  const selectedSubscription = useMemo(
    () =>
      subscriptions.find(
        (subscription) => subscription.child_uuid === selectedChildUuid,
      ),
    [subscriptions, selectedChildUuid],
  );

  const selectedChild = useMemo(
    () =>
      user.visibleChildren.find((child) => child.uuid === selectedChildUuid),
    [user.visibleChildren, selectedChildUuid],
  );

  let content;

  if (!selectedSubscription && selectedChild) {
    content = <LayoutNoSubscription t={t} childUuid={selectedChildUuid} />;
  } else if (!selectedSubscription || !selectedChild) {
    content = <LayoutNoSubscription t={t} />;
  } else if (isSubscriptionFinished(selectedSubscription)) {
    content = (
      <LayoutAgedOutSubscription
        t={t}
        selectedSubscription={selectedSubscription}
      />
    );
  } else if (isSubscriptionCancelled(selectedSubscription)) {
    content = (
      <LayoutCancelledSubscription
        t={t}
        userUuid={selectedSubscription.recipient_uuid}
        subscriptionUuid={selectedSubscription.uuid}
      />
    );
  } else {
    content = (
      <LayoutActiveSubscription
        t={t}
        selectedChild={selectedChild}
        selectedSubscription={selectedSubscription}
        user={user}
        noticeBanner={noticeBanner}
        holidayBanner={holidayBanner}
        prepayBanner={prepayBanner}
      />
    );
  }

  const formattedItems: TypeItem[] = useMemo(
    () =>
      user.visibleChildren.map((item, index) => ({
        id: item.uuid,
        title: item.name,
        icon: childrenIcons[index % childrenIcons.length],
      })),
    [user.visibleChildren, childrenIcons],
  );

  return (
    <div data-testid="playKitSchedule" className="mb-20">
      <ItemSelector
        items={formattedItems}
        selectedItemId={selectedChildUuid}
        setSelectedItemId={setSelectedChildUuid}
        trackCtaName={CTA_NAMES.CHILD_SELECTED}
        trackPageLocation={PAGE_LOCATIONS.PLAY_KITS_PAGE}
        hasEditChild
        editChildText={t.edit}
        hasAddChild
        addChildTitle={t.addChild}
      />

      <SubscriptionPageHeading t={t} selectedChild={selectedChild} />

      <Divider />

      {content}
    </div>
  );
}

export default PlayKitSchedule;
